import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Popover,
  Select,
  Snackbar,
  Stack,
  Slider,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CalendarMonth, Close, Person, UploadFile } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { DateRangePicker } from 'react-date-range';
import { addDays, format, isSameDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { keyframes } from '@mui/system';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import {
  pageFilterActionButtonSx,
  pageFilterAutocompleteSx,
  pageFilterDatePickerSx,
  pageFilterFormControlSx,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  createTodayDateRange,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  isDefaultDateTimeRange,
} from '../utils/filterDefaults';
import { AuditTile } from './Audit';
import { AuditProcessingTimelineModal } from './AuditDialogs';
import { FindPersonSearchFromTileModal } from './FindPersonSearchFromTileModal';
import AuditFindPersonDialogs from './AuditFindPersonDialogs';
import { findPersonActivity, getCameraOptions, getActivityLogs, getAuditImageBlob } from '../utils/api';
import { peek as peekAuditImage } from '../services/auditImageCache';
import useAuditImagePrefetch from '../hooks/useAuditImagePrefetch';
import { filterPersonNameOptions, formatPersonNameOptionLabel, sanitizeNameSearchInput } from '../utils/nameSearchInput';
import { PersonSearch } from './PersonSearch';

const DEFAULT_PAGE_LIMIT = 25;
const primary = '#40518A';
const INITIAL_START_TIME = '00:00';
const INITIAL_END_TIME = '23:59';

const searchingBorderGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(64, 81, 138, 0.20);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(64, 81, 138, 0.10);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(64, 81, 138, 0.20);
  }
`;

// Same shimmer + tile skeleton pattern as Audit logs (In/Out) loading state
const activityLogsShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const activityLogsShimmerStyles = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: `${activityLogsShimmer} 1.5s ease-in-out infinite`,
};

const ActivityLogsTileSkeleton = () => (
  <Card
    sx={{
      width: '100%',
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box sx={{ width: '100%', paddingTop: '100%', position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0, ...activityLogsShimmerStyles }} />
    </Box>
    <CardContent sx={{ p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ width: '50%', height: 18, borderRadius: 1, ...activityLogsShimmerStyles }} />
        <Box sx={{ width: 60, height: 22, borderRadius: 11, ...activityLogsShimmerStyles }} />
      </Stack>
      <Box sx={{ width: '70%', height: 14, borderRadius: 1, mb: 0.5, ...activityLogsShimmerStyles }} />
      <Box sx={{ width: '40%', height: 12, borderRadius: 1, ...activityLogsShimmerStyles }} />
    </CardContent>
  </Card>
);

const defaultDateRange = () => ([
  { startDate: new Date(), endDate: new Date(), key: 'selection' },
]);

const parseDateParam = (value) => {
  if (!value) return null;
  const parsed = new Date(`${String(value).trim()}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normaliseMs = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return Math.trunc(num);
};

const msToIsoString = (value) => {
  const num = normaliseMs(value);
  if (num === null) return '';
  const date = new Date(num);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const parseIsoToEpochMs = (value) => {
  if (!value && value !== 0) return Number.NaN;
  const text = String(value).trim();
  if (!text) return Number.NaN;
  const hasTimezone = /([+-]\d{2}:?\d{2}|Z)$/i.test(text);
  const normalizedText = hasTimezone ? text : `${text}Z`;
  const parsed = Date.parse(normalizedText);
  if (!Number.isNaN(parsed)) return parsed;
  return Number.NaN;
};

const resolveTimestamp = (isoValue, msValue, fallbackIso) => {
  let resolvedIso = isoValue || '';
  let resolvedMs = normaliseMs(msValue);
  if (resolvedMs === null && fallbackIso) {
    const parsed = parseIsoToEpochMs(fallbackIso);
    if (!Number.isNaN(parsed)) {
      resolvedMs = parsed;
    }
  }
  if (resolvedMs !== null && resolvedMs !== undefined) {
    const canonicalIso = msToIsoString(resolvedMs);
    if (canonicalIso) {
      resolvedIso = canonicalIso;
    }
  } else if (resolvedIso) {
    const parsed = parseIsoToEpochMs(resolvedIso);
    if (!Number.isNaN(parsed)) {
      resolvedMs = parsed;
      const canonicalIso = msToIsoString(resolvedMs);
      if (canonicalIso) {
        resolvedIso = canonicalIso;
      }
    }
  }
  return { iso: resolvedIso, ms: resolvedMs };
};

const getCapturedTimestampForActivityLog = (log) => {
  if (!log) return { iso: '', ms: null };
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.captured_at || meta.captured_at,
    log.captured_at_ms ?? meta.captured_at_ms,
    log.created_at
  );
};

const getDateLabel = (iso) => {
  if (!iso) return 'Unknown Date';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return 'Unknown Date';
  return format(dt, 'MMM dd, yyyy');
};

const groupLogsByDate = (logs) => {
  return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
    const key = getDateLabel(log?.captured_at || log?.created_at);
    if (!acc[key]) acc[key] = [];
    acc[key].push(log);
    return acc;
  }, {});
};

const ActivityLogs = ({ embedded = false, active = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const urlParams = new URLSearchParams(location.search);
  const initialStartDate = parseDateParam(urlParams.get('start_date')) || new Date();
  const initialEndDate = parseDateParam(urlParams.get('end_date')) || initialStartDate;
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ limit: DEFAULT_PAGE_LIMIT, offset: 0, total: 0, current_page: 1 });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    { startDate: initialStartDate, endDate: initialEndDate, key: 'selection' },
  ]);
  const [draftDateRange, setDraftDateRange] = useState([
    { startDate: initialStartDate, endDate: initialEndDate, key: 'selection' },
  ]);
  const [startTime, setStartTime] = useState(INITIAL_START_TIME);
  const [endTime, setEndTime] = useState(INITIAL_END_TIME);
  const [draftStartTime, setDraftStartTime] = useState(INITIAL_START_TIME);
  const [draftEndTime, setDraftEndTime] = useState(INITIAL_END_TIME);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [availableNames, setAvailableNames] = useState([]);
  const [selectedPersonIdForSearch, setSelectedPersonIdForSearch] = useState(
    (urlParams.get('person_id') || '').trim(),
  );

  /** [] */
  const [autoCompleteOptionsLable, setAutoCompleteOptionsLable] = useState([]);
  const [cameraId, setCameraId] = useState('');
  /** '' = all; backend GET /activity/logs uses status=matched | possible_match | unknown */
  const [activityStatusFilter, setActivityStatusFilter] = useState('');
  const [activityCameras, setActivityCameras] = useState([]);
  const [timingDialog, setTimingDialog] = useState({ open: false, log: null, stack: [] });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const show = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Find Person (Audit-style) state for Activity Logs
  const [imageSourceDialogOpen, setImageSourceDialogOpen] = useState(false);
  const [previewImageDataUrl, setPreviewImageDataUrl] = useState(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [uploadMatchLoading, setUploadMatchLoading] = useState(false);
  const [refreshingFindPerson, setRefreshingFindPerson] = useState(false);
  const [findPersonSimilarityThreshold, setFindPersonSimilarityThreshold] = useState('0.3');
  const [findPersonSimilarityThresholdDraft, setFindPersonSimilarityThresholdDraft] = useState('0.3');
  const [findPersonQueryImageBase64, setFindPersonQueryImageBase64] = useState('');
  const [findPersonResultMode, setFindPersonResultMode] = useState(false);
  const [findPersonSummary, setFindPersonSummary] = useState({
    known_count: 0,
    possible_match_count: 0,
    unknown_count: 0,
    total_count: 0,
  });
  const [findPersonSearchIconActivityLogsModalOpen, setFindPersonSearchIconActivityLogsModalOpen] = useState(false);
  const [findPersonSearchIconActivityLogsLoading, setFindPersonSearchIconActivityLogsLoading] = useState(false);
  const [findPersonSearchIconActivityLogsStackImages, setFindPersonSearchIconActivityLogsStackImages] = useState([]);
  const [findPersonSearchIconActivityLogsSelectedImage, setFindPersonSearchIconActivityLogsSelectedImage] = useState(null);
  const [findPersonSearchIconActivityLogsSelectedId, setFindPersonSearchIconActivityLogsSelectedId] = useState(null);
  const [findPersonSearchIconActivityLogsThreshold, setFindPersonSearchIconActivityLogsThreshold] = useState('0.3');
  const [matchedPersonIdFilter, setMatchedPersonIdFilter] = useState('');
  const [matchedPersonName, setMatchedPersonName] = useState('');
  const uploadMatchInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const prevFindPersonResultModeRef = useRef(false);
  const findPersonRefreshTimerRef = useRef(null);
  const lastFindPersonRefreshRef = useRef({ key: '', at: 0 });
  const activityCamerasRequestRef = useRef(null);
  const activityCamerasLoadedRef = useRef(false);

  const selectedStart = dateRange[0]?.startDate || new Date();
  const selectedEnd = dateRange[0]?.endDate || selectedStart;
  const buildDateTimeRange = useCallback((range, startTimeValue, endTimeValue) => {
    const rangeStart = range?.[0]?.startDate || new Date();
    const rangeEnd = range?.[0]?.endDate || rangeStart;

    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const [startHour, startMinute] = parseTime(startTimeValue, 0, 0);
    const [endHour, endMinute] = parseTime(endTimeValue, 23, 59);

    // Build local date/time in user's browser timezone, then serialize to UTC ISO.
    const startDateTimeUTC = new Date(rangeStart);
    startDateTimeUTC.setHours(startHour, startMinute, 0, 0);
    const endDateTimeUTC = new Date(rangeEnd);
    endDateTimeUTC.setHours(endHour, endMinute, 59, 0);

    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
    };
  }, []);
  const { formattedStart, formattedEnd } = useMemo(
    () => buildDateTimeRange(dateRange, startTime, endTime),
    [buildDateTimeRange, dateRange, startTime, endTime]
  );
  const safeAvailableNames = Array.isArray(availableNames) ? availableNames : [];
  const groupedLogs = useMemo(() => groupLogsByDate(logs), [logs]);
  const datePickerOpen = Boolean(datePickerAnchor);

  const timingDialogResultMeta = useMemo(() => {
    const log = timingDialog.log;
    if (!log) return { label: 'Unknown', color: 'default' };
    if (log.confidence_level === 'possible_match') return { label: 'Possible Match', color: 'warning' };
    if (log.success) return { label: 'Matched', color: 'success' };
    if (log.confidence_level === 'no_match') return { label: 'Unmatched', color: 'error' };
    return { label: 'Failed', color: 'error' };
  }, [timingDialog.log]);

  const timingDialogPrefetchIds = useMemo(() => {
    if (!timingDialog.open) return [];
    const ids = [];
    if (timingDialog.log?.audit_id) ids.push(timingDialog.log.audit_id);
    (timingDialog.stack || []).forEach((frame) => {
      if (frame?.log?.audit_id) ids.push(frame.log.audit_id);
    });
    return ids;
  }, [timingDialog.open, timingDialog.log, timingDialog.stack]);
  useAuditImagePrefetch(timingDialogPrefetchIds, timingDialog.open);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });

  const handleUploadMatchClick = () => {
    if (!uploadMatchLoading && !loading) {
      setImageSourceDialogOpen(true);
    }
  };

  const handleCloseImageSourceDialog = () => {
    setImageSourceDialogOpen(false);
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
      setIsCameraOn(false);
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
    setPreviewImageDataUrl(null);
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
        audio: false,
      });
      cameraStreamRef.current = mediaStream;
      setIsCameraOn(true);
      setTimeout(() => {
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      setIsCameraOn(false);
    }
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    setIsCameraOn(false);
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  };

  const capturePhotoFromCamera = async () => {
    if (!cameraVideoRef.current) return;
    try {
      const video = cameraVideoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        stopCamera();
        setPreviewImageDataUrl(dataUrl);
      }, 'image/jpeg', 0.95);
    } catch (error) {
      console.error('Error capturing photo:', error);
    }
  };

  const normalizeImageSourceForFindPerson = async (imageSource) => {
    const source = typeof imageSource === 'string' ? imageSource : '';
    if (!source) {
      throw new Error('No image selected for find person');
    }
    if (source.startsWith('data:image/')) {
      return source;
    }
    if (source.startsWith('blob:')) {
      const response = await fetch(source);
      if (!response.ok) {
        throw new Error('Failed to read selected image');
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
    if (/^[A-Za-z0-9+/=]+$/.test(source)) {
      return source;
    }
    throw new Error('Invalid image source for find person');
  };

  const clearUploadedMatchFilter = useCallback(() => {
    setFindPersonResultMode(false);
    setFindPersonQueryImageBase64('');
    setFindPersonSummary({
      known_count: 0,
      possible_match_count: 0,
      unknown_count: 0,
      total_count: 0,
    });
    setMatchedPersonIdFilter('');
    setMatchedPersonName('');
    setPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
  }, []);

  const closeTimingDialog = useCallback(() => {
    setTimingDialog({ open: false, log: null, stack: [] });
  }, []);

  const openTimingDialog = useCallback((log, image) => {
    const normalizedLog = {
      ...(log || {}),
      operation_type: 'activity',
      camera: log?.camera || log?.request_metadata?.camera_label || log?.request_metadata?.camera_type || 'ACTIVITY',
      request_metadata: {
        ...(log?.request_metadata || {}),
      },
    };
    setTimingDialog({
      open: true,
      log: normalizedLog,
      stack: [{ log: normalizedLog, image: image || null }],
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    const params = new URLSearchParams(location.search);
    const hasStart = Boolean(params.get('start_date'));
    const hasEnd = Boolean(params.get('end_date'));
    if (hasStart && hasEnd) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    if (!hasStart) params.set('start_date', today);
    if (!hasEnd) params.set('end_date', today);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [active, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!active) return;
    const params = new URLSearchParams(location.search);
    const nextStart = parseDateParam(params.get('start_date')) || new Date();
    const nextEnd = parseDateParam(params.get('end_date')) || nextStart;
    setDateRange((prev) => {
      const prevStart = prev[0]?.startDate;
      const prevEnd = prev[0]?.endDate;
      if (
        prevStart?.toDateString() === nextStart.toDateString() &&
        prevEnd?.toDateString() === nextEnd.toDateString()
      ) {
        return prev;
      }
      return [{ startDate: nextStart, endDate: nextEnd, key: 'selection' }];
    });
    setDraftDateRange((prev) => {
      const prevStart = prev[0]?.startDate;
      const prevEnd = prev[0]?.endDate;
      if (
        prevStart?.toDateString() === nextStart.toDateString() &&
        prevEnd?.toDateString() === nextEnd.toDateString()
      ) {
        return prev;
      }
      return [{ startDate: nextStart, endDate: nextEnd, key: 'selection' }];
    });
  }, [active, location.search]);

  // Keep the selected person filter in the URL just like the other filters.
  useEffect(() => {
    if (!active) return;
    const params = new URLSearchParams(location.search);
    const next = (params.get('person_id') || '').trim();
    setSelectedPersonIdForSearch((prev) => (prev === next ? prev : next));
  }, [active, location.search]);

  // Sync the selected person filter back to the URL.
  useEffect(() => {
    if (!active) return;
    const params = new URLSearchParams(location.search);
    const current = (params.get('person_id') || '').trim();
    const next = (selectedPersonIdForSearch || '').trim();
    if (current === next) return;
    if (next) params.set('person_id', next); else params.delete('person_id');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [active, selectedPersonIdForSearch, location.pathname, location.search, navigate]);

  const loadLogs = useCallback(async (limit, offset) => {
    if (!active) return;
    if (findPersonResultMode) return;
    try {
      setLoading(true);
      const res = await getActivityLogs({
        start_time: formattedStart,
        end_time: formattedEnd,
        person_name: selectedName || undefined,
        camera_id: cameraId || undefined,
        status: activityStatusFilter || undefined,
        limit,
        offset,
        person_id: selectedPersonIdForSearch || undefined,
      });
      const arr = Array.isArray(res?.logs) ? res.logs : [];
      const apiLimit = Number(res?.pagination?.limit) || limit;
      const apiOffset = Number(res?.pagination?.offset) || offset;
      const total = Number(res?.pagination?.total) || 0;
      setLogs(arr);
      setPagination({
        limit: apiLimit,
        offset: apiOffset,
        total,
        current_page: Math.floor(apiOffset / apiLimit) + 1,
      });
    } catch (error) {
      console.error('Failed to load activity logs:', error);
      setLogs([]);
      setPagination((prev) => ({ ...prev, limit, offset, total: 0, current_page: Math.floor(offset / limit) + 1 }));
    } finally {
      setLoading(false);
    }
  }, [active, activityStatusFilter, cameraId, formattedEnd, formattedStart, selectedName, findPersonResultMode,
    selectedPersonIdForSearch
  ]);

  useEffect(() => {
    if (!active) return;
    if (findPersonResultMode) return;
    loadLogs(pagination.limit, pagination.offset);
  }, [active, loadLogs, pagination.limit, pagination.offset, findPersonResultMode, selectedPersonIdForSearch]);

  const processImageForMatching = useCallback(async (
    dataUrl,
    {
      isRefresh = false,
      limit = null,
      offset = null,
      similarityThreshold: overrideThreshold = null,
    } = {}
  ) => {
    try {
      if (isRefresh) {
        setRefreshingFindPerson(true);
        setLogs([]);
      } else {
        setUploadMatchLoading(true);
      }

      const thresholdValue = overrideThreshold !== null ? overrideThreshold : findPersonSimilarityThreshold;
      const parsedThreshold = Number(thresholdValue);
      const similarityThreshold = Number.isFinite(parsedThreshold)
        ? Math.max(0, Math.min(1, parsedThreshold))
        : undefined;

      const normalizedImageBase64 = await normalizeImageSourceForFindPerson(dataUrl);
      setFindPersonQueryImageBase64(normalizedImageBase64);

      const currentLimit = limit !== null ? limit : pagination.limit;
      const currentOffset = offset !== null ? offset : (isRefresh ? 0 : 0);

      const matchResponse = await findPersonActivity({
        image_base64: normalizedImageBase64,
        start_time: formattedStart,
        end_time: formattedEnd,
        name: selectedName.trim(),
        camera: cameraId || '',
        similarity_threshold: similarityThreshold,
        limit: currentLimit,
        offset: currentOffset,
      });

      const combinedLogs = Array.isArray(matchResponse?.combined_matches)
        ? matchResponse.combined_matches
        : [];
      const knownCount = Number(matchResponse?.known_count || 0);
      const possibleMatchCount = Number(matchResponse?.possible_match_count || 0);
      const unknownCount = Number(matchResponse?.unknown_count || 0);
      const totalCount = Number(matchResponse?.total_count || combinedLogs.length);
      const matchedPersonId = String(matchResponse?.recognized_person_id || '').trim();
      const matchedPersonNameValue = String(matchResponse?.recognized_name || '').trim();

      const responsePagination = matchResponse?.pagination;
      const paginationTotal = responsePagination?.total ?? totalCount;
      const paginationLimit = responsePagination?.limit ?? currentLimit;
      const paginationOffset = responsePagination?.offset ?? currentOffset;
      const paginationCurrentPage = responsePagination?.current_page ?? Math.floor(paginationOffset / paginationLimit) + 1;

      setLogs(combinedLogs);
      setPagination((prev) => ({
        ...prev,
        limit: paginationLimit,
        offset: paginationOffset,
        current_page: paginationCurrentPage,
        total: paginationTotal,
      }));
      setMatchedPersonIdFilter(matchedPersonId);
      setMatchedPersonName(matchedPersonNameValue);
      setFindPersonSummary({
        known_count: knownCount,
        possible_match_count: possibleMatchCount,
        unknown_count: unknownCount,
        total_count: totalCount,
      });
      setFindPersonResultMode(true);
      setFindPersonSimilarityThresholdDraft(String(similarityThreshold ?? thresholdValue));

      if (!isRefresh) {
        if (matchedPersonId) {
          show(
            `Found ${totalCount} result(s): matched ${knownCount}, possible match ${possibleMatchCount}, unmatched ${unknownCount} for ${matchedPersonNameValue || matchedPersonId}`,
            'success'
          );
        } else {
          show(`No known person match. Found ${unknownCount} result(s) in unmatched logs`, 'info');
        }
      }
    } catch (e) {
      const msg = e?.message || 'Failed to match image';
      if (msg.toLowerCase().includes('multiple faces detected')) {
        clearUploadedMatchFilter();
        show(msg, 'warning');
      } else if (msg.toLowerCase().includes('no face detected')) {
        clearUploadedMatchFilter();
        show(msg, 'warning');
      } else {
        show(msg, 'error');
      }
    } finally {
      if (isRefresh) {
        setRefreshingFindPerson(false);
      } else {
        setUploadMatchLoading(false);
      }
    }
  }, [
    cameraId,
    clearUploadedMatchFilter,
    findPersonSimilarityThreshold,
    formattedEnd,
    formattedStart,
    pagination.limit,
    selectedName,
    show,
  ]);

  // Employee page → Find Person in Logs → "Activity logs": open Activity with same state as In/Out → Audit.
  useEffect(() => {
    if (!active) return;
    if (!location.state?.findPersonImage) return;

    const { findPersonImage, personId, personName, similarityThreshold } = location.state;

    setLogs([]);
    setPagination((prev) => ({
      ...prev,
      offset: 0,
      current_page: 1,
      total: 0,
    }));

    setFindPersonResultMode(true);
    navigate(location.pathname, { replace: true, state: {} });

    setFindPersonSummary({
      known_count: 0,
      possible_match_count: 0,
      unknown_count: 0,
      total_count: 0,
    });
    setMatchedPersonIdFilter(personId != null && String(personId).trim() !== '' ? String(personId).trim() : '');
    setMatchedPersonName(personName != null && String(personName).trim() !== '' ? String(personName).trim() : '');

    setFindPersonQueryImageBase64(findPersonImage);
    if (similarityThreshold !== undefined && similarityThreshold !== null) {
      const t = String(similarityThreshold);
      setFindPersonSimilarityThreshold(t);
      setFindPersonSimilarityThresholdDraft(t);
    }

    setUploadMatchLoading(true);
    processImageForMatching(findPersonImage, { isRefresh: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirror Audit: react to navigation state once per delivery
  }, [active, location.state]);

  const handleCloseFindPersonSearchIconActivityLogs = useCallback(() => {
    setFindPersonSearchIconActivityLogsModalOpen(false);
    setFindPersonSearchIconActivityLogsStackImages([]);
    setFindPersonSearchIconActivityLogsSelectedImage(null);
    setFindPersonSearchIconActivityLogsSelectedId(null);
  }, []);

  const handleOpenFindPersonSearchIconActivityLogs = useCallback(async (log, image, stackImages = []) => {
    if (!log || !log.audit_id) return;

    try {
      setFindPersonSearchIconActivityLogsModalOpen(true);
      setFindPersonSearchIconActivityLogsSelectedImage(null);
      setFindPersonSearchIconActivityLogsSelectedId(null);
      setFindPersonSearchIconActivityLogsThreshold(String(findPersonSimilarityThreshold || '0.3'));

      const allImages = [];

      if (image) {
        allImages.push({ log, image, isMain: true, uniqueId: log.audit_id || `main-${Date.now()}` });
      } else if (log.audit_id) {
        try {
          const blob = await getAuditImageBlob(log.audit_id);
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          allImages.push({ log, image: dataUrl, isMain: true, uniqueId: log.audit_id });
        } catch (error) {
          console.error('Error loading main image:', error);
        }
      }

      if (Array.isArray(stackImages) && stackImages.length > 0) {
        for (let stackIdx = 0; stackIdx < stackImages.length; stackIdx += 1) {
          const stackItem = stackImages[stackIdx];
          if (stackItem.log && stackItem.log.audit_id && stackItem.log.audit_id !== log.audit_id) {
            let stackImage = stackItem.image;
            if (!stackImage && stackItem.log.audit_id) {
              try {
                const blob = await getAuditImageBlob(stackItem.log.audit_id);
                stackImage = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
              } catch (error) {
                console.error(`Error loading stack image for ${stackItem.log.audit_id}:`, error);
                continue;
              }
            }
            if (stackImage) {
              allImages.push({
                log: stackItem.log,
                image: stackImage,
                isMain: false,
                uniqueId: stackItem.log.audit_id || `stack-${Date.now()}-${stackIdx}`,
              });
            }
          }
        }
      }

      setFindPersonSearchIconActivityLogsStackImages(allImages);

      if (allImages.length > 0) {
        const firstItem = allImages[0];
        setFindPersonSearchIconActivityLogsSelectedImage(firstItem.image);
        setFindPersonSearchIconActivityLogsSelectedId(firstItem.uniqueId);
      }
    } catch (error) {
      console.error('Error opening Find Person search (activity logs):', error);
      show('Failed to open search', 'error');
    }
  }, [findPersonSimilarityThreshold, show]);

  const handleSearchFindPersonSearchIconActivityLogs = useCallback(async () => {
    if (!findPersonSearchIconActivityLogsSelectedImage) {
      show('Please select an image to search', 'warning');
      return;
    }

    try {
      setFindPersonSearchIconActivityLogsLoading(true);

      const selectedImage = findPersonSearchIconActivityLogsSelectedImage;
      const threshold = findPersonSearchIconActivityLogsThreshold;

      setFindPersonSimilarityThreshold(threshold);
      setFindPersonSimilarityThresholdDraft(String(threshold));

      handleCloseFindPersonSearchIconActivityLogs();

      await processImageForMatching(selectedImage, {
        isRefresh: false,
        similarityThreshold: threshold,
      });
    } catch (error) {
      console.error('Error searching activity logs:', error);
      show(error?.message || 'Failed to search activity logs', 'error');
    } finally {
      setFindPersonSearchIconActivityLogsLoading(false);
    }
  }, [
    findPersonSearchIconActivityLogsSelectedImage,
    findPersonSearchIconActivityLogsThreshold,
    handleCloseFindPersonSearchIconActivityLogs,
    processImageForMatching,
    show,
  ]);

  const handleUploadMatchChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      show('Please upload a valid image file', 'error');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setPreviewImageDataUrl(dataUrl);
    } catch (e) {
      console.error(e?.message || 'Failed to read uploaded image');
      show(e?.message || 'Failed to read uploaded image', 'error');
    }
  };

  const handleFindPersonFromPreview = async () => {
    if (!previewImageDataUrl) return;
    setImageSourceDialogOpen(false);
    await processImageForMatching(previewImageDataUrl);
    setPreviewImageDataUrl(null);
  };

  // Auto-refresh results while in find-person mode when threshold/filters change.
  // Debounced to avoid multiple API calls while dragging the slider.
  useEffect(() => {
    if (!active) return;

    const justEnteredResultMode =
      findPersonResultMode && !prevFindPersonResultModeRef.current;
    prevFindPersonResultModeRef.current = findPersonResultMode;

    // Avoid double-calling the API: the first search already ran processImageForMatching().
    if (justEnteredResultMode) return;

    if (!findPersonResultMode || !findPersonQueryImageBase64) return;
    if (uploadMatchLoading || refreshingFindPerson) return;

    // React 18 StrictMode (dev) can run effects twice; also multiple state updates
    // can re-trigger this effect. Dedupe by a stable request key.
    const refreshKey = JSON.stringify({
      img: String(findPersonQueryImageBase64 || ''),
      start: String(formattedStart || ''),
      end: String(formattedEnd || ''),
      name: String(selectedName || ''),
      cameraId: String(cameraId || ''),
      threshold: String(findPersonSimilarityThreshold || ''),
      limit: Number(pagination.limit) || 0,
      offset: 0,
    });
    const now = Date.now();
    if (
      lastFindPersonRefreshRef.current.key === refreshKey &&
      now - (lastFindPersonRefreshRef.current.at || 0) < 1500
    ) {
      return;
    }

    if (findPersonRefreshTimerRef.current) {
      clearTimeout(findPersonRefreshTimerRef.current);
      findPersonRefreshTimerRef.current = null;
    }

    findPersonRefreshTimerRef.current = setTimeout(() => {
      lastFindPersonRefreshRef.current = { key: refreshKey, at: Date.now() };
      processImageForMatching(findPersonQueryImageBase64, {
        isRefresh: true,
        limit: pagination.limit,
        offset: 0,
      });
    }, 400);

    return () => {
      if (findPersonRefreshTimerRef.current) {
        clearTimeout(findPersonRefreshTimerRef.current);
        findPersonRefreshTimerRef.current = null;
      }
    };
  }, [
    active,
    cameraId,
    findPersonSimilarityThreshold,
    findPersonResultMode,
    findPersonQueryImageBase64,
    pagination.limit,
    processImageForMatching,
    refreshingFindPerson,
    selectedName,
    uploadMatchLoading,
    formattedStart,
    formattedEnd,
  ]);

  useEffect(() => {
    if (!active) return;
    setPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
  }, [active, selectedName, cameraId, formattedStart, formattedEnd]);

  const ensureActivityCamerasLoaded = useCallback(() => {
    if (activityCamerasLoadedRef.current) {
      return Promise.resolve();
    }
    if (activityCamerasRequestRef.current) {
      return activityCamerasRequestRef.current;
    }
    activityCamerasRequestRef.current = getCameraOptions({ usage: 'activity' })
      .then((res) => {
        setActivityCameras(Array.isArray(res?.cameras) ? res.cameras : []);
      })
      .catch(() => {
        setActivityCameras([]);
      })
      .finally(() => {
        activityCamerasLoadedRef.current = true;
        activityCamerasRequestRef.current = null;
      });
    return activityCamerasRequestRef.current;
  }, []);

  const openDatePicker = (event) => {
    const current = dateRange[0] || { startDate: new Date(), endDate: new Date(), key: 'selection' };
    setDraftDateRange([{ startDate: current.startDate, endDate: current.endDate, key: 'selection' }]);
    setDraftStartTime(startTime);
    setDraftEndTime(endTime);
    setDatePickerAnchor(event.currentTarget);
  };

  const applyDateRange = () => {
    const next = draftDateRange[0] || { startDate: new Date(), endDate: new Date(), key: 'selection' };
    const params = new URLSearchParams(location.search);
    params.set('start_date', format(next.startDate, 'yyyy-MM-dd'));
    params.set('end_date', format(next.endDate, 'yyyy-MM-dd'));
    navigate({ pathname: location.pathname, search: params.toString() });
    setStartTime(draftStartTime || INITIAL_START_TIME);
    setEndTime(draftEndTime || INITIAL_END_TIME);
    setPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
    setDatePickerAnchor(null);
  };

  const applyNameSelection = (value) => {
    const sanitized = sanitizeNameSearchInput(value || '');
    setNameInput(sanitized);
    setSelectedName(sanitized);
  };

  const hasActiveFilters = useMemo(() => (
    !!(selectedName?.trim()) ||
    !!cameraId ||
    !!activityStatusFilter ||
    findPersonResultMode ||
    !isDefaultDateTimeRange(dateRange, startTime, endTime)
  ), [
    activityStatusFilter,
    cameraId,
    dateRange,
    endTime,
    findPersonResultMode,
    selectedName,
    startTime,
  ]);

  const handleClearAllFilters = useCallback(() => {
    if (findPersonResultMode) {
      clearUploadedMatchFilter();
    }
    setSelectedPersonIdForSearch("")
    setSelectedName('');
    setNameInput('');
    setCameraId('');
    setActivityStatusFilter('');
    const todayRange = createTodayDateRange();
    setDateRange(todayRange);
    setDraftDateRange(todayRange);
    setStartTime(INITIAL_START_TIME);
    setEndTime(INITIAL_END_TIME);
    setDraftStartTime(INITIAL_START_TIME);
    setDraftEndTime(INITIAL_END_TIME);
    setPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
    const params = new URLSearchParams(location.search);
    params.set('start_date', format(todayRange[0].startDate, 'yyyy-MM-dd'));
    params.set('end_date', format(todayRange[0].endDate, 'yyyy-MM-dd'));
    params.delete('start_time');
    params.delete('end_time');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [clearUploadedMatchFilter, findPersonResultMode, location.pathname, location.search, navigate]);
  const activitylogSearchQueryParams = useMemo(() => ({
    offset: 0,
    include_deleted: 'false',
  
  }), []);
  const formatEnrolledPersonNameOption = useCallback((option) => {
    return formatPersonNameOptionLabel(option, { includeCode: true });
  }, []);
  const filterEnrolledPersonNameOptions = useCallback((options, state) => {
    return filterPersonNameOptions(options, state);
  }, []);

  return (
    <Container maxWidth="2200" disableGutters sx={{ px: 0 }}>
      {!embedded ? (
        <PageHeader
          title="Activity"
          subtitle="Audit-style view of activity recognitions"
          icon={null}
        />
      ) : null}

      <Box sx={pageFiltersToolbarSx}>
        <Stack {...pageFiltersStackSx}>
          <Box sx={pageFiltersControlsSx}>
            <PersonSearch
              queryParams={activitylogSearchQueryParams}
              onSelect={(emp) => {
                if (!emp || !emp.person_id) return;
                setSelectedPersonIdForSearch(emp.person_id);
              }}
              onQueryChange={() => {
                // Typing/clearing the name field should fall back to a name
                // search, dropping the selected person_id filter.
                setSelectedPersonIdForSearch('');
              }}
            />


            <FormControl size="small" sx={pageFilterFormControlSx(200)}>
              <InputLabel id="activity-camera-label" shrink>
                Camera
              </InputLabel>
              <Select
                labelId="activity-camera-label"
                label="Camera"
                value={cameraId}
                onOpen={() => { ensureActivityCamerasLoaded(); }}
                onChange={(event) => setCameraId(event.target.value)}
                displayEmpty
                renderValue={(v) => (v ? (activityCameras.find((c) => (c.camera_id || '') === v)?.label || v) : 'All')}
              >
                <MenuItem value="">All</MenuItem>
                {activityCameras.map((camera) => (
                  <MenuItem key={camera.camera_id || camera.label} value={camera.camera_id || ''}>
                    {camera.label || camera.camera_id || 'Activity Camera'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={pageFilterFormControlSx(200)}>
              <InputLabel id="activity-status-label" shrink>
                Select Status
              </InputLabel>
              <Select
                labelId="activity-status-label"
                label="Select Status"
                value={activityStatusFilter}
                onChange={(event) => {
                  setActivityStatusFilter(event.target.value);
                  setPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
                }}
                displayEmpty
                renderValue={(v) => {
                  if (!v) return 'All';
                  if (v === 'matched') return 'Matched';
                  if (v === 'possible_match') return 'Possible Match';
                  if (v === 'unknown') return 'Unmatched';
                  return v;
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="matched">Matched</MenuItem>
                <MenuItem value="possible_match">Possible Match</MenuItem>
                <MenuItem value="unknown">Unmatched</MenuItem>
              </Select>
            </FormControl>

            <Box
              onClick={openDatePicker}
              sx={pageFilterDatePickerSx(220)}
            >
              <CalendarMonth fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {isSameDay(selectedStart, selectedEnd)
                  ? `${format(selectedStart, 'MMM dd, yyyy')}${startTime !== INITIAL_START_TIME || endTime !== INITIAL_END_TIME ? ` • ${startTime} - ${endTime}` : ''}`
                  : `${format(selectedStart, 'MMM dd')} - ${format(selectedEnd, 'MMM dd, yyyy')}${startTime !== INITIAL_START_TIME || endTime !== INITIAL_END_TIME ? ` • ${startTime} - ${endTime}` : ''}`}
              </Typography>
            </Box>

            <ClearAllFiltersButton
              onClear={handleClearAllFilters}
              disabled={!hasActiveFilters}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          </Box>
          <Box sx={pageFiltersActionsSx}>
            <Tooltip title="Upload image to find person in logs">
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFile />}
                onClick={handleUploadMatchClick}
                disabled={uploadMatchLoading || refreshingFindPerson || loading}
                sx={{
                  textTransform: 'none',
                  height: 40,
                  width: { xs: '100%', md: '150px' },
                  ...(uploadMatchLoading ? {
                    borderColor: primary,
                    color: primary,
                    animation: `${searchingBorderGlow} 1.4s ease-in-out infinite`,
                    '& .MuiButton-startIcon': { color: primary },
                    '&.Mui-disabled': {
                      borderColor: primary,
                      color: primary,
                    },
                  } : {}),
                }}
              >
                {uploadMatchLoading ? 'Searching...' : 'Find Person'}
              </Button>
            </Tooltip>
          </Box>
          <Popover
            open={datePickerOpen}
            anchorEl={datePickerAnchor}
            onClose={() => setDatePickerAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: { maxWidth: '100vw', overflow: 'auto' },
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
              <Box
                sx={{
                  p: 2,
                  borderRight: { md: '1px solid' },
                  borderBottom: { xs: '1px solid', md: 'none' },
                  borderColor: 'divider',
                  minWidth: 140,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Quick Select
                </Typography>
                <Stack spacing={0.5}>
                  {[
                    { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                    { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                    { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                    { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                    {
                      label: 'This Month',
                      getValue: () => {
                        const now = new Date();
                        return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                      },
                    },
                    {
                      label: 'Last Month',
                      getValue: () => {
                        const now = new Date();
                        return { startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1), endDate: new Date(now.getFullYear(), now.getMonth(), 0) };
                      },
                    },
                  ].map((preset) => (
                    <Button
                      key={preset.label}
                      size="small"
                      variant="text"
                      onClick={() => {
                        const { startDate: presetStart, endDate: presetEnd } = preset.getValue();
                        setDraftDateRange([{ startDate: presetStart, endDate: presetEnd, key: 'selection' }]);
                      }}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 1.5, py: 0.75, color: 'text.primary', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Stack>
              </Box>

              <Box
                sx={{
                  '& .rdrCalendarWrapper': { fontSize: { xs: '0.8rem', sm: '0.875rem' } },
                  '& .rdrMonth': { width: { xs: '280px', sm: '300px' } },
                }}
              >
                <DateRangePicker
                  ranges={draftDateRange}
                  onChange={(ranges) => setDraftDateRange([ranges.selection])}
                  moveRangeOnFirstSelection={false}
                  months={isMobile ? 1 : 2}
                  direction={isMobile ? 'vertical' : 'horizontal'}
                  rangeColors={[primary]}
                  staticRanges={[]}
                  inputRanges={[]}
                  maxDate={new Date()}
                />
                <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    size="small"
                    value={draftStartTime}
                    onChange={(e) => setDraftStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    fullWidth
                  />
                  <TextField
                    label="End Time"
                    type="time"
                    size="small"
                    value={draftEndTime}
                    onChange={(e) => setDraftEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    fullWidth
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                  <Button
                    onClick={() => {
                      const today = new Date();
                      setDraftDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                      setDraftStartTime(INITIAL_START_TIME);
                      setDraftEndTime(INITIAL_END_TIME);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Reset
                  </Button>
                  <Button onClick={() => setDatePickerAnchor(null)} variant="outlined" size="small">
                    Cancel
                  </Button>
                  <Button onClick={applyDateRange} variant="contained" size="small">
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Popover>
        </Stack>
      </Box>

      {findPersonResultMode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
            flexWrap: 'wrap',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            width: '100%',
            backgroundColor: 'background.paper',
            boxShadow: (theme) => theme.shadows[1],
            mb: 2,
          }}
        >
          <Box sx={{ width: { xs: '100%', sm: 280 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Similarity Threshold: {parseFloat(findPersonSimilarityThreshold || 0).toFixed(2)}
            </Typography>
            <Slider
              value={parseFloat(findPersonSimilarityThresholdDraft || findPersonSimilarityThreshold || 0.3)}
              onChange={(e, newValue) => setFindPersonSimilarityThresholdDraft(String(newValue))}
              onChangeCommitted={(e, newValue) => setFindPersonSimilarityThreshold(String(newValue))}
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: '0' },
                { value: 0.25, label: '0.25' },
                { value: 0.5, label: '0.5' },
                { value: 0.75, label: '0.75' },
                { value: 1, label: '1' },
              ]}
              sx={{ width: '100%' }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: { xs: '1 1 100%', md: '1 1 360px' },
              minWidth: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 1.25,
              py: 0,
              backgroundColor: 'grey.50',
              height: 44,
              overflow: 'hidden',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0, flexWrap: 'nowrap' }}>
              <Chip
                icon={<Person fontSize="small" />}
                color="default"
                variant="outlined"
                size="small"
                label={matchedPersonName || matchedPersonIdFilter || 'Unmatched person'}
                sx={{
                  maxWidth: { xs: '100%', sm: 320 },
                  '& .MuiChip-label': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
              <Chip
                color="success"
                variant="filled"
                size="small"
                label={`Matched ${findPersonSummary.known_count}`}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                color="warning"
                variant="filled"
                size="small"
                label={`Possible Match ${findPersonSummary.possible_match_count}`}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                color="error"
                variant="filled"
                size="small"
                label={`Unmatched ${findPersonSummary.unknown_count}`}
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: { xs: 0, md: 'auto' },
              height: 44,
            }}
          >
            <Tooltip title="Clear find person result">
              <IconButton
                size="small"
                onClick={clearUploadedMatchFilter}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  backgroundColor: 'grey.50',
                  '&:hover': {
                    color: 'error.main',
                    borderColor: 'error.light',
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  },
                  width: 44,
                  height: 44,
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {(loading || (uploadMatchLoading && findPersonResultMode) || (refreshingFindPerson && findPersonResultMode)) ? (
        <Box>
          <Box sx={{ width: 180, height: 28, borderRadius: 1, mb: 2, ...activityLogsShimmerStyles }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(5, 1fr)',
                xl: 'repeat(6, 1fr)',
              },
              gap: 2,
            }}
          >
            {Array.from({ length: 12 }).map((_, idx) => (
              <ActivityLogsTileSkeleton key={`activity-logs-skel-${idx}`} />
            ))}
          </Box>
        </Box>
      ) : logs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>No activity logs found</Typography>
          <Typography variant="body2" color="text.secondary">
            Adjust filters or widen the date range to see results.
          </Typography>
        </Paper>
      ) : (
        Object.entries(groupedLogs).map(([date, items]) => (
          <Box key={date} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
              {date}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)',
                  lg: 'repeat(5, 1fr)',
                  xl: 'repeat(6, 1fr)',
                },
                gap: 2,
              }}
            >
              {items.map((log, index) => (
                <AuditTile
                  key={`${log.audit_id || log.created_at}-${index}`}
                  log={log}
                  lazyLoad
                  capturedTs={{ iso: log.captured_at || log.created_at }}
                  timeOnly
                  disableRecordingPlayback
                  onClick={() => openTimingDialog(log, log.audit_id ? peekAuditImage(log.audit_id) : null)}
                  onSearchClick={(tileLog, tileImage) => handleOpenFindPersonSearchIconActivityLogs(tileLog, tileImage, [])}
                  findPersonResultMode={findPersonResultMode}
                />
              ))}
            </Box>
          </Box>
        ))
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Showing {pagination.total === 0 ? 0 : pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} results
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">Tiles per page:</Typography>
            <Select
              size="small"
              value={pagination.limit}
              onChange={(event) => setPagination((prev) => ({ ...prev, limit: Number(event.target.value), offset: 0, current_page: 1 }))}
              sx={{ minWidth: 90 }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={75}>75</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>
        </Stack>
        <Pagination
          count={Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit))}
          page={pagination.current_page}
          onChange={(event, page) => setPagination((prev) => ({ ...prev, offset: (page - 1) * prev.limit, current_page: page }))}
          showFirstButton
          showLastButton
        />
      </Box>

      <AuditProcessingTimelineModal
        timingDialog={timingDialog}
        closeTimingDialog={closeTimingDialog}
        isMobile={isMobile}
        timingDialogResultMeta={timingDialogResultMeta}
        hasTimingDialogTrackAttendance={false}
        modalFrames={Array.isArray(timingDialog.stack) ? timingDialog.stack : []}
        imageCache={{}}
        ANONYMOUS_IMAGE="/anonymous.webp"
        showPossibleMatchActions={false}
        showProcessingTimeline={false}
        showTimingHeading={false}
        showTopMatches={false}
        title="Activity details"
      />

      <FindPersonSearchFromTileModal
        open={findPersonSearchIconActivityLogsModalOpen}
        onClose={handleCloseFindPersonSearchIconActivityLogs}
        onSearch={handleSearchFindPersonSearchIconActivityLogs}
        loading={findPersonSearchIconActivityLogsLoading}
        selectedImage={findPersonSearchIconActivityLogsSelectedImage}
        primary={primary}
        stackImages={findPersonSearchIconActivityLogsStackImages}
        selectedItemId={findPersonSearchIconActivityLogsSelectedId}
        onSelectItem={(item) => {
          setFindPersonSearchIconActivityLogsSelectedImage(item.image);
          setFindPersonSearchIconActivityLogsSelectedId(item.uniqueId);
        }}
        getCapturedTimestamp={getCapturedTimestampForActivityLog}
        threshold={findPersonSearchIconActivityLogsThreshold}
        onThresholdChange={setFindPersonSearchIconActivityLogsThreshold}
        introText="Select an image from the bucket/stack to use for Find Person search. The search will display results in the main activity logs view."
      />

      <AuditFindPersonDialogs
        imageSourceDialogOpen={imageSourceDialogOpen}
        handleCloseImageSourceDialog={handleCloseImageSourceDialog}
        findPersonSimilarityThreshold={findPersonSimilarityThreshold}
        setFindPersonSimilarityThreshold={setFindPersonSimilarityThreshold}
        isCameraOn={isCameraOn}
        previewImageDataUrl={previewImageDataUrl}
        uploadMatchInputRef={uploadMatchInputRef}
        handleUploadMatchChange={handleUploadMatchChange}
        primary={primary}
        startCamera={startCamera}
        stopCamera={stopCamera}
        capturePhotoFromCamera={capturePhotoFromCamera}
        cameraVideoRef={cameraVideoRef}
        handleFindPersonFromPreview={handleFindPersonFromPreview}
        uploadMatchLoading={uploadMatchLoading}
        unknownConfirmOpen={false}
        setUnknownConfirmOpen={() => { }}
        handleStartUnknownSearch={() => { }}
        unknownMatchRunning={false}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default ActivityLogs;